/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import styles from "gui/visual/header.module.css";
import { QueryVisual, VisualQueryDetails } from "lib/visuals/types";
import { VisualItemProps } from "gui/visual/visual";
import { queryPath } from "lib/links/links";
import { renderMarkdown } from "lib/html/markdown";
import { useTheme } from "gui/theme/theme";
import { Parameter } from "lib/parameters/types";
import { paramQueryStringKey } from "lib/parameters/query";

export const VisualHeader: React.FC<VisualItemProps<QueryVisual>> = (props) => {
  const theme = useTheme();
  return (
    <header className={styles.header}>
      <div className={styles.details}>
        <div
          className={styles.title}
          style={{ color: theme.chart_title_color }}
        >
          <HeaderLink {...props} />
        </div>
        <HeaderDescription {...props} />
      </div>
    </header>
  );
};

function getDashboardPageParameters(
  query_details: Pick<VisualQueryDetails, "query_id" | "parameters"> | undefined
): Parameter[] {
  if (!query_details) {
    return [];
  }

  const { parameters, query_id } = query_details;

  const parametersWithQueryId = parameters.map((param) => ({
    query_id,
    ...param,
  }));

  return parametersWithQueryId;
}

function toQueryPageParameter(parameter: Parameter): Parameter {
  return {
    ...parameter,
    query_id: undefined,
    default_value: undefined,
  };
}

// Returns the query params needed to link to the query with the same
// applied params as on the dashboard page
function useQueryParams(
  query_details: Pick<VisualQueryDetails, "query_id" | "parameters"> | undefined
) {
  const router = useRouter();
  const parameters = getDashboardPageParameters(query_details);

  return Object.fromEntries(
    parameters
      .map((param) => {
        const key = paramQueryStringKey(param);
        const value = router.query[key];
        const queryPageKey = paramQueryStringKey(toQueryPageParameter(param));

        return [queryPageKey, value];
      })
      .filter(([_key, value]) => value !== undefined)
  );
}

export type HeaderLinkProps = Pick<
  VisualItemProps<QueryVisual>,
  "visual" | "queryName" | "iframe" | "queryId" | "title"
>;

export const HeaderLink: React.FC<HeaderLinkProps> = (props) => {
  const href = {
    pathname: queryPath(props.queryId, props.visual.id),
    query: useQueryParams(props.visual.query_details),
  };

  const visualName = props.visual.name || "";
  const visualNameIsDefaultNames =
    visualName === "Chart" || visualName === "Table";
  let title = props.title;
  let subTitle = "";

  // If no title is set for the header link try infer
  // it from the query name and visual name
  if (!title) {
    // The prefered layout it visualName -> queryName
    // however if the visual name is empty or is the default only show
    // the query name
    if (visualNameIsDefaultNames || !visualName) {
      title = props.queryName;
    } else {
      title = visualName;
      subTitle = props.queryName || "";
    }
  }

  return (
    <Link href={href}>
      <a target={props.iframe ? "_blank" : undefined} className={styles.link}>
        <span>{title}</span>
        {subTitle && <span>{subTitle}</span>}
      </a>
    </Link>
  );
};

const HeaderDescription: React.FC<VisualItemProps> = (props) => {
  const theme = useTheme();

  if (!props.queryDescription) {
    return null;
  }

  return (
    <div
      className={styles.description}
      style={{ color: theme.description_color }}
      dangerouslySetInnerHTML={{
        __html: renderMarkdown(props.queryDescription, true),
      }}
    />
  );
};
